"use strict";
/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

Object.defineProperty(exports, "__esModule", {
  value: true
});

var PropertyModelSpec_1 = require("../PropertyModelSpec");

var ConditionalFormattingSpec_1 = require("../ConditionalFormattingSpec");

var RuleConstants_1 = require("../../../definitions/constants/RuleConstants");

var DEFAULT_FORMAT = 'defaultFormat';
exports.DEFAULT_FORMAT = DEFAULT_FORMAT;
var DEFAULT_PALETTE_ID = 'condColorPalette0';
exports.DEFAULT_PALETTE_ID = DEFAULT_PALETTE_ID;
var BLACKLISTED_ATTR = ['summaryFillColor', 'fillColor', 'backgroundOpacity'];
var DEFAULT_BACKGROUND_OPACITY = 50;

var ConditionalFormatSpecHelper =
/** @class */
function () {
  function ConditionalFormatSpecHelper() {}

  ConditionalFormatSpecHelper.isKpiVis = function (visType) {
    return visType === 'KPI';
  };

  ConditionalFormatSpecHelper.getDefaultFormat = function (dataItemId, visType) {
    var spec = ConditionalFormatSpecHelper.getDefaultConditionalFormat(dataItemId, visType);
    return ConditionalFormatSpecHelper.createConditionalFormatSpec(spec, visType);
  };

  ConditionalFormatSpecHelper.createConditionalFormatSpec = function (formatSpec, visType) {
    if (!formatSpec) {
      return null;
    }

    var dataItemId = formatSpec.dataItemId;
    var paletteId = formatSpec.paletteId;
    var colorBy = formatSpec.colorBy;
    var backgroundOpacity = formatSpec.backgroundOpacity;
    var scale = formatSpec.scale;
    var heatId = formatSpec.heatId;
    var newFormat = new ConditionalFormattingSpec_1.ConditionalFormatSpec({
      dataItemId: dataItemId,
      paletteId: paletteId,
      colorBy: colorBy,
      backgroundOpacity: backgroundOpacity,
      scale: scale,
      rules: [],
      heatId: heatId
    }, visType);

    if (formatSpec.rules && formatSpec.rules.length > 0) {
      formatSpec.rules.forEach(function (rule) {
        var ruleType = rule.ruleType;
        var startCondition = rule.startCondition ? new PropertyModelSpec_1.Condition(rule.startCondition) : undefined;
        var endCondition = rule.endCondition ? new PropertyModelSpec_1.Condition(rule.endCondition,
        /* isStartCondition */
        false) : undefined;
        var ruleStyle = new PropertyModelSpec_1.RuleStyle(rule.ruleStyle, visType);
        var operator = rule.operator;
        var ruleToAdd = new PropertyModelSpec_1.Rule({
          ruleType: ruleType,
          startCondition: startCondition,
          endCondition: endCondition,
          ruleStyle: ruleStyle,
          operator: operator
        });
        newFormat.addRule(-1, ruleToAdd);
      });
    }

    return newFormat;
  };

  ConditionalFormatSpecHelper.getDefaultConditionalFormat = function (dataItemId, visType) {
    var isKpi = ConditionalFormatSpecHelper.isKpiVis(visType);
    var defaultFormat = {
      dataItemId: dataItemId,
      paletteId: DEFAULT_PALETTE_ID,
      colorBy: isKpi ? undefined : dataItemId,
      backgroundOpacity: isKpi ? undefined : DEFAULT_BACKGROUND_OPACITY,
      scale: RuleConstants_1.RULE_SCALES.PERCENTAGE,
      rules: []
    };

    if (isKpi) {
      var firstRule = ConditionalFormatSpecHelper.getDefaultRuleThreshold(visType, RuleConstants_1.RULE_OPERATORS.lessThan, 'color4', RuleConstants_1.RULE_STYLE_SHAPE.downArrow);
      var secondRule = ConditionalFormatSpecHelper.getDefaultRuleThreshold(visType, RuleConstants_1.RULE_OPERATORS.greaterThanOrEqualTo);
      defaultFormat.rules.push(firstRule);
      defaultFormat.rules.push(secondRule);
    }

    return defaultFormat;
  };

  ConditionalFormatSpecHelper.createNewRule = function (spec, visType) {
    if (spec === void 0) {
      spec = {};
    }

    var ruleType = spec['ruleType'];
    var startCondition = new PropertyModelSpec_1.Condition(spec['startCondition']);
    var endCondition = spec['endCondition'] ? new PropertyModelSpec_1.Condition(spec['endCondition'],
    /* isStartCondition */
    false) : undefined;
    var ruleStyle = new PropertyModelSpec_1.RuleStyle(spec['ruleStyle'], visType);
    var operator = spec['operator'];
    var expand = spec['expand'] || false;
    return new PropertyModelSpec_1.Rule({
      ruleType: ruleType,
      startCondition: startCondition,
      endCondition: endCondition,
      ruleStyle: ruleStyle,
      operator: operator,
      expand: expand
    });
  };

  ConditionalFormatSpecHelper.getDefaultRuleThreshold = function (visType, startConditionType, textColor, shape) {
    if (startConditionType === void 0) {
      startConditionType = undefined;
    }

    if (textColor === void 0) {
      textColor = 'color0';
    }

    if (shape === void 0) {
      shape = RuleConstants_1.RULE_STYLE_SHAPE.upArrow;
    }

    var isKpi = ConditionalFormatSpecHelper.isKpiVis(visType);
    return {
      ruleType: RuleConstants_1.RULE_TYPES.THRESHOLD,
      operator: RuleConstants_1.RangeOperators.none,
      startCondition: {
        type: startConditionType
      },
      ruleStyle: {
        textColor: textColor,
        fillColor: isKpi ? undefined : '',
        shape: shape
      }
    };
  };

  ConditionalFormatSpecHelper.getDefaultRuleRange = function (visType, textColor, shape) {
    if (textColor === void 0) {
      textColor = 'color1';
    }

    if (shape === void 0) {
      shape = RuleConstants_1.RULE_STYLE_SHAPE.upArrow;
    }

    var isKpi = ConditionalFormatSpecHelper.isKpiVis(visType);
    return {
      ruleType: RuleConstants_1.RULE_TYPES.RANGE,
      operator: RuleConstants_1.RangeOperators.and,
      startCondition: {
        type: RuleConstants_1.RULE_OPERATORS.greaterThanOrEqualTo
      },
      endCondition: {
        type: RuleConstants_1.RULE_OPERATORS.lessThanOrEqualTo
      },
      ruleStyle: {
        textColor: textColor,
        fillColor: isKpi ? undefined : '',
        shape: shape
      }
    };
  };

  ConditionalFormatSpecHelper.getConditionTypeFromRuleOperator = function (value) {
    switch (value) {
      case RuleConstants_1.RULE_OPERATORS.greaterThan:
        return RuleConstants_1.CONDITION_TYPES.GT;

      case RuleConstants_1.RULE_OPERATORS.greaterThanOrEqualTo:
        return RuleConstants_1.CONDITION_TYPES.GTE;

      case RuleConstants_1.RULE_OPERATORS.lessThan:
        return RuleConstants_1.CONDITION_TYPES.LT;

      case RuleConstants_1.RULE_OPERATORS.lessThanOrEqualTo:
        return RuleConstants_1.CONDITION_TYPES.LTE;

      case RuleConstants_1.RULE_OPERATORS.equalTo:
        return RuleConstants_1.CONDITION_TYPES.ET;

      case RuleConstants_1.RULE_OPERATORS.inTop:
        return RuleConstants_1.CONDITION_TYPES.IT;

      case RuleConstants_1.RULE_OPERATORS.inBottom:
        return RuleConstants_1.CONDITION_TYPES.IB;

      case RuleConstants_1.RULE_OPERATORS.missing:
        return RuleConstants_1.CONDITION_TYPES.MI;

      default:
        throw new Error('Invalid RULE_OPERATORS');
        break;
    }
  };

  ConditionalFormatSpecHelper.getRuleOperatorFromConditionType = function (value) {
    switch (value) {
      case RuleConstants_1.CONDITION_TYPES.GT:
        return RuleConstants_1.RULE_OPERATORS.greaterThan;

      case RuleConstants_1.CONDITION_TYPES.GTE:
        return RuleConstants_1.RULE_OPERATORS.greaterThanOrEqualTo;

      case RuleConstants_1.CONDITION_TYPES.LT:
        return RuleConstants_1.RULE_OPERATORS.lessThan;

      case RuleConstants_1.CONDITION_TYPES.LTE:
        return RuleConstants_1.RULE_OPERATORS.lessThanOrEqualTo;

      case RuleConstants_1.CONDITION_TYPES.ET:
        return RuleConstants_1.RULE_OPERATORS.equalTo;

      case RuleConstants_1.CONDITION_TYPES.IT:
        return RuleConstants_1.RULE_OPERATORS.inTop;

      case RuleConstants_1.CONDITION_TYPES.IB:
        return RuleConstants_1.RULE_OPERATORS.inBottom;

      case RuleConstants_1.CONDITION_TYPES.MI:
        return RuleConstants_1.RULE_OPERATORS.missing;

      default:
        throw new Error('Invalid CONDITION_TYPES');
        break;
    }
  };

  ConditionalFormatSpecHelper.isPercentScale = function (scale) {
    return scale === RuleConstants_1.RULE_SCALES.PERCENTAGE;
  };

  ConditionalFormatSpecHelper.getDefaultConditionValue = function (isStartCondition, scale, minValue, maxValue) {
    void scale;
    void minValue; // @todo just return 100 for now since this is the default for KPI base on initial percent scale of 100

    if (isStartCondition) {
      return ConditionalFormatSpecHelper.isPercentScale(scale) ? RuleConstants_1.RULE_DEFAULT_MAX_VALUE : maxValue;
    } // @todo percent of 0 and maxValue make no sense here
    // The story to implement pre-populate base on selected condition and scale will fix this


    return ConditionalFormatSpecHelper.isPercentScale(scale) ? RuleConstants_1.RULE_DEFAULT_MIN_VALUE : minValue;
  };

  ConditionalFormatSpecHelper.updateBlackListedAttrBaseOnVisType = function (visType, attrName, blackListedList) {
    if (blackListedList === void 0) {
      blackListedList = [];
    }

    if (BLACKLISTED_ATTR.includes(attrName)) {
      var idx = blackListedList.indexOf(attrName);

      if (idx === -1) {
        // Add attrName for KPI
        if (ConditionalFormatSpecHelper.isKpiVis(visType)) {
          blackListedList.push(attrName);
        }
      } else {
        // Remove attrName for Xtab/Table
        blackListedList.splice(idx, 1);
      }
    } else {
      throw new Error("Invalid attribute: " + attrName + " to be blacklisted");
    }
  };

  ConditionalFormatSpecHelper.updateConditionalFormatSpecBasedOnVisType = function (visType, conditionalFormatSpec) {
    var isKpi = ConditionalFormatSpecHelper.isKpiVis(visType);

    if (isKpi) {
      conditionalFormatSpec.setColorBy(undefined);
      conditionalFormatSpec.setBackgroundOpacity(undefined);
    } else {
      if (!conditionalFormatSpec.getColorBy()) {
        conditionalFormatSpec.setColorBy(conditionalFormatSpec.getDataItemId());
      }

      if (!conditionalFormatSpec.getBackgroundOpacity()) {
        conditionalFormatSpec.setBackgroundOpacity(DEFAULT_BACKGROUND_OPACITY);
      }
    }

    return conditionalFormatSpec;
  };

  return ConditionalFormatSpecHelper;
}();

exports.ConditionalFormatSpecHelper = ConditionalFormatSpecHelper;
exports.default = ConditionalFormatSpecHelper;
